import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users as UsersIcon, Shield, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('User role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-indigo-50 text-indigo-700';
      case 'accountant':
        return 'bg-emerald-50 text-emerald-700';
      case 'viewer':
        return 'bg-slate-50 text-slate-700';
      default:
        return 'bg-slate-50 text-slate-700';
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="user-management-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8">
          <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="users-title">
            User Management
          </h1>
          <p className="text-slate-600 mt-1 font-ibm">Manage user roles and permissions</p>
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="users-loading">Loading users...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Role</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Joined</th>
                    <th className="px-6 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-slate-600 font-ibm" data-testid="no-users">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${index}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-900 text-white rounded-sm flex items-center justify-center font-chivo font-bold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-ibm font-medium text-slate-900">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <Select
                            value={user.role}
                            onValueChange={(newRole) => handleRoleUpdate(user.id, newRole)}
                          >
                            <SelectTrigger data-testid={`role-select-${index}`} className="w-40 bg-slate-50 border-slate-200 rounded-sm">
                              <div className="flex items-center gap-2">
                                <Shield size={14} />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="accountant">Accountant</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleDelete(user.id)}
                            data-testid={`delete-user-${index}`}
                            className="p-2 hover:bg-red-50 rounded-sm transition-colors inline-flex items-center"
                            title="Delete User"
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-slate-50 border border-slate-200 p-6 rounded-sm"
          data-testid="role-info"
        >
          <h3 className="font-chivo font-bold text-slate-900 mb-4">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                <span className="font-ibm font-semibold text-slate-900">Admin</span>
              </div>
              <p className="text-sm text-slate-600 font-ibm">Full access to all features, including user management</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-emerald-600 rounded-full" />
                <span className="font-ibm font-semibold text-slate-900">Accountant</span>
              </div>
              <p className="text-sm text-slate-600 font-ibm">Can add, edit, and delete transactions, view all reports</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-slate-600 rounded-full" />
                <span className="font-ibm font-semibold text-slate-900">Viewer</span>
              </div>
              <p className="text-sm text-slate-600 font-ibm">Read-only access to dashboards and reports</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default UserManagement;